import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import {
  ExpandCollapsePanel,
  IconButton,
  Offscreen
} from '@deque/cauldron-react';
import IGTIcon from './IGTIcon';
import IntelligentGuidedRun from './IntelligentGuidedRun';
import useMediaQuery from '../../common/hooks/useMediaQuery';
import getGuides, { type GuideConfig } from '../../common/utils/get-guides';
import styles from './TestDetails.css';
import {
  getBaseFilters,
  getSearchParamsFromFilters
} from '../../common/hooks/useIssuesFilter';
import {
  type UniversalIssueWithScreenshot,
  type Filters,
  manifestUtils
} from '@deque/axe-devtools-issues-utils';
import { REPLAY_ENABLED_GUIDE_NAMES } from '../utils/constants';

interface IntelligentGuidedTestsProps {
  issues: UniversalIssueWithScreenshot[];
  manifestRerunGroupsByType: manifestUtils.ManifestRerunGroupsByType;
  filters: Filters;
}

interface IntelligentGuidedTestProps extends IntelligentGuidedTestsProps {
  guide: GuideConfig;
}

interface IGTRunIssueTextProps {
  runCount: number;
  issueCount: number;
  issueUrl: string;
  heading: string;
  name: string;
}

const IGTRunIssueText: React.FC<IGTRunIssueTextProps> = ({
  runCount,
  issueCount,
  issueUrl,
  heading,
  name
}) => {
  const narrow = useMediaQuery('(max-width: 31.25rem)');

  const IGTRunText =
    runCount === 1 ? (
      <Trans>
        <strong>1</strong> run
      </Trans>
    ) : (
      <Trans>
        <strong>{{ runCount: runCount || 0 }}</strong> runs
      </Trans>
    );

  if (!issueCount) {
    return narrow ? (
      <span>
        <Trans>
          <strong>0</strong> <Offscreen>{heading}</Offscreen> issues
        </Trans>
      </span>
    ) : (
      <span>
        {IGTRunText} &middot;{' '}
        <Trans>
          <strong>0</strong> <Offscreen>{heading}</Offscreen> total issues
        </Trans>
      </span>
    );
  }

  if (issueCount === 1) {
    return narrow ? (
      <Link to={issueUrl}>
        <Trans>
          <strong>1</strong> <Offscreen>{heading}</Offscreen> issue
        </Trans>
      </Link>
    ) : (
      <span>
        {IGTRunText} &middot;{' '}
        <Link
          to={issueUrl}
          aria-labelledby={`${name}-igt-issues-link ${name}-igt-heading`}
          id={`${name}-igt-issues-link`}
        >
          <Trans>
            <strong>1</strong> <Offscreen>{heading}</Offscreen> total issue
          </Trans>
        </Link>
      </span>
    );
  }

  return narrow ? (
    <Link to={issueUrl}>
      <Trans>
        <strong>{{ issueCount: issueCount }}</strong> issues
      </Trans>
    </Link>
  ) : (
    <span>
      {IGTRunText} &middot;{' '}
      <Link
        to={issueUrl}
        aria-labelledby={`${name}-igt-issues-link ${name}-igt-heading`}
        id={`${name}-igt-issues-link`}
      >
        <Trans>
          <strong>{{ issueCount: issueCount }}</strong> total issues
        </Trans>
      </Link>
    </span>
  );
};

const IntelligentGuidedTest: React.FC<IntelligentGuidedTestProps> = ({
  guide,
  issues,
  manifestRerunGroupsByType,
  filters
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const { name, heading, deprecated } = guide;
  const issueCount = issues.filter(issue => issue.igtId === name).length;
  const igtRerunGroups = manifestRerunGroupsByType[name] || [];
  const searchParams = getSearchParamsFromFilters({
    ...getBaseFilters(filters),
    igt: name
  });

  const IGTIssueUrl = `${location.pathname}/issues?${searchParams}`;
  const runsId = `${name}-runs`;

  const toggleExpanded = () => setOpen(!open);

  // Only show deprecated IGTs when there are issues present
  if (deprecated && !issueCount) {
    return null;
  }

  return (
    <div className={classNames('Accordion', { 'Accordion--expanded': open })}>
      <div className="Accordion__trigger">
        <div className={styles.igt__header}>
          <h3
            className={styles.igt__heading}
            onClick={toggleExpanded}
            id={`${name}-igt-heading`}
          >
            <IGTIcon
              type={deprecated ? 'igt-deprecated' : name}
              aria-hidden={true}
            />
            {heading}
          </h3>
          <IGTRunIssueText
            runCount={igtRerunGroups.length}
            issueCount={issueCount}
            issueUrl={IGTIssueUrl}
            heading={heading}
            name={name}
          />
        </div>
        <IconButton
          label={
            open
              ? t('Close {{heading}} IGT Panel', { heading })
              : t('Open {{heading}} IGT Panel', { heading })
          }
          icon={open ? 'triangle-down' : 'triangle-right'}
          aria-controls={runsId}
          aria-expanded={open}
          onClick={toggleExpanded}
        />
      </div>
      <ExpandCollapsePanel id={runsId} className="Accordion__panel" open={open}>
        <div
          className={
            !igtRerunGroups.length ? styles.igt__noruns : styles.igt__runs
          }
        >
          {igtRerunGroups.length ? (
            igtRerunGroups.map(runOrRerunGroup => {
              const currentRun = manifestUtils.getCurrentManifestForRerunGroup(
                runOrRerunGroup as manifestUtils.ManifestRerunGroup
              );
              const rerunGroup = REPLAY_ENABLED_GUIDE_NAMES.includes(name)
                ? (runOrRerunGroup as manifestUtils.ManifestRerunGroup)
                : null;

              return (
                <IntelligentGuidedRun
                  key={currentRun.id}
                  run={currentRun}
                  rerunGroup={rerunGroup}
                  filters={filters}
                  issues={issues}
                />
              );
            })
          ) : (
            <p>{t(`There are 0 IGT runs for {{ tool}}.`, { tool: heading })}</p>
          )}
        </div>
      </ExpandCollapsePanel>
    </div>
  );
};

const IntelligentGuidedTests: React.FC<IntelligentGuidedTestsProps> = ({
  issues,
  manifestRerunGroupsByType,
  filters
}) => {
  const { t } = useTranslation();
  const guides = getGuides(t);

  return (
    <section className={styles.igt} aria-labelledby="intelligent-guided-tests">
      <h2 id="intelligent-guided-tests">{t('Intelligent Guided Tests')}</h2>
      {guides.map(guide => (
        <IntelligentGuidedTest
          key={guide.name}
          guide={guide}
          issues={issues}
          manifestRerunGroupsByType={manifestRerunGroupsByType}
          filters={filters}
        />
      ))}
    </section>
  );
};

export default IntelligentGuidedTests;
